import { ArrowDown, ArrowUpRight, Camera, Mic, ScanLine } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { analytics } from '../services/analytics';
import { APP_STORE_URL } from '../constants/site';

const HeroProductProof = lazy(() => import('./HeroProductProof').then((module) => ({ default: module.HeroProductProof })));

const captureModes = [
    { label: 'Photo', icon: Camera },
    { label: 'Voice', icon: Mic },
    { label: 'Barcode', icon: ScanLine },
];

export const Hero = ({ showProductProof }: { showProductProof: boolean }) => (
    <section id="main-content" className="relative overflow-hidden px-4 pb-16 pt-28 sm:px-6 sm:pt-36 lg:min-h-[860px] lg:pb-20 lg:pt-36 xl:min-h-[920px] xl:pb-24 xl:pt-40">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute left-1/2 top-[-18rem] h-[44rem] w-[44rem] -translate-x-1/2 rounded-full bg-primary/[0.08] blur-[120px]" />
            <div className="hero-grid absolute inset-0 opacity-40" />
            <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-background to-transparent" />
        </div>

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:gap-6 xl:grid-cols-[minmax(0,0.98fr)_minmax(0,1.02fr)] xl:gap-4">
            <div className="min-w-0 lg:-translate-y-16 xl:-translate-y-24">
                <p className="eyebrow">
                    <span className="h-2 w-2 rounded-full bg-primary shadow-[0_0_16px_rgba(190,239,22,0.9)]" />
                    Now on the App Store · iOS
                </p>
                <h1 className="mt-7 max-w-3xl text-[clamp(2.65rem,13.6vw,5.75rem)] font-black leading-[0.84] tracking-[-0.07em] text-white sm:text-[clamp(5rem,12vw,7rem)] lg:text-[clamp(4.5rem,6.5vw,6.25rem)] xl:text-[clamp(5rem,6vw,6.75rem)]">
                    <span className="block whitespace-nowrap">KNOW <span className="ml-[0.08em]">WHAT</span></span>
                    <span className="block">TO DO</span>
                    <span className="block text-primary">NEXT.</span>
                </h1>
                <p className="mt-8 max-w-xl text-lg leading-8 text-text-secondary sm:text-xl">
                    Nurvo connects nutrition, training and recovery in one clear daily view. Nova turns the patterns into a practical next step, not another dashboard to decode.
                </p>

                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="button-primary" onClick={() => analytics.appStoreClick('hero', 'Download on the App Store')}>
                        Download on the App Store <ArrowUpRight size={18} aria-hidden="true" />
                    </a>
                    <a href="#product" className="button-secondary" onClick={() => analytics.ctaClick('hero', 'Explore the app')}>
                        Explore the app <ArrowDown size={18} aria-hidden="true" />
                    </a>
                </div>

                <div className="mt-10 flex flex-wrap gap-2 sm:gap-3" aria-label="Meal capture options">
                    {captureModes.map(({ label, icon: Icon }) => (
                        <div key={label} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 text-sm text-text-secondary">
                            <Icon size={15} className="text-primary" aria-hidden="true" />
                            {label}
                        </div>
                    ))}
                </div>
            </div>

            {showProductProof ? <Suspense fallback={<div className="h-[500px] w-full sm:h-[650px] lg:h-[640px] xl:h-[680px]" aria-hidden="true" />}><HeroProductProof /></Suspense> : <div className="h-[500px] w-full sm:h-[650px] lg:h-[640px] xl:h-[680px]" aria-hidden="true" />}
        </div>
    </section>
);
